import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProjects } from '../services/webService';
import { arraySort } from '../utils/arraySort';
import { translate } from '../utils/translations';
import { useAuth } from '../context/AuthContext';

// Project categories mapping
// Games: GameShips(0), AircraftWar(1), SpermaGame(2), EspantaChiringuitos(5), SpermGame2(8), MasterPanic(10), ThieveOS(11), MathBubbleShooter(25), MathJoyas(26)
// Mobile Apps: Repeat(3), AlertsApp(4), RackTrack(6), Timerin(7), CalenSport(9)
// Web: GranCanariaOfflineMap(12), EduRestas(16), EduSumas(17), EduMulti(18), EduIngles(19)
// AI: CancerClassifier(13), PeopleDetection(14), PeopleSegmentation(15), LLMWebSocket(20), QuizAI(21), AndroidPeopleDetection(22), AndroidNureonAI(23), AndroidNureonAIPlus(24)
const PROJECT_CATEGORIES = {
  ai: ['cancer_classifier', 'people_detection', 'people_segmentation', 'llm_websocket', 'quiz_ai', 'android_people_detection', 'android_nureonai', 'android_nureonai_plus'],
  mobile: ['repeat', 'alertsapp', 'racktrack', 'timerin', 'calensport'],
  games: ['gameships', 'aircraftwar', 'spermagame', 'espanta_chiringuitos', 'spermgame2', 'master_panic', 'thieveos', 'math_bubble_shooter_multi', 'math_joyas_multi'],
  web: ['gran_canaria_offline_map', 'edu_restas', 'edu_sumas', 'edu_multi', 'edu_ingles']
};

function AppNavbar() {
  const [projects, setProjects] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dashboardDropdownOpen, setDashboardDropdownOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [navbarOpen, setNavbarOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dashboardDropdownRef = useRef(null);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const projectList = getProjects();
    setProjects(projectList);
  }, []);

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setActiveCategory(null);
      }
      if (dashboardDropdownRef.current && !dashboardDropdownRef.current.contains(event.target)) {
        setDashboardDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setNavbarOpen(false);
    navigate('/home');
  };

  const getProjectsByCategory = (categoryKey) => {
    const ids = PROJECT_CATEGORIES[categoryKey] || [];
    return projects.filter(p => ids.includes(p.id));
  };

  const handleCategoryClick = (categoryKey) => {
    if (activeCategory === categoryKey) {
      setActiveCategory(null);
    } else {
      setActiveCategory(categoryKey);
    }
  };

  const handleProjectClick = () => {
    setDropdownOpen(false);
    setActiveCategory(null);
    setNavbarOpen(false);
  };

  const categories = [
    { key: 'ai', icon: 'fas fa-robot', label: 'category_ai' },
    { key: 'mobile', icon: 'fas fa-mobile-alt', label: 'category_mobile' },
    { key: 'games', icon: 'fas fa-gamepad', label: 'category_games' },
    { key: 'web', icon: 'fas fa-globe', label: 'category_web' }
  ];

  return (
    <nav className="navbar navbar-expand-md navbar-dark bg-dark fixed-top">
      <Link className="navbar-brand" to="/">CloudsApps</Link>
      <button className="navbar-toggler" type="button"
        onClick={() => setNavbarOpen(!navbarOpen)}
        aria-controls="navbarsExampleDefault"
        aria-expanded={navbarOpen}
        aria-label="Toggle navigation">
        <span className="navbar-toggler-icon"></span>
      </button>
      <div className={`collapse navbar-collapse ${navbarOpen ? 'show' : ''}`} id="navbarsExampleDefault">
        <ul className="navbar-nav mr-auto">
          <li className="nav-item active">
            <Link className="nav-link" to="/home" onClick={() => setNavbarOpen(false)}>Home
              <span className="sr-only">(current)</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link className="nav-link" to="/maps" onClick={() => setNavbarOpen(false)}>Maps</Link>
          </li>

          {/* Projects dropdown with 4 subcategories */}
          <li className={`nav-item dropdown ${dropdownOpen ? 'show' : ''}`} ref={dropdownRef}>
            <a className="nav-link dropdown-toggle" style={{ cursor: 'pointer' }}
              id="dropdown01"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-haspopup="true"
              aria-expanded={dropdownOpen}>
              {translate('projects')}
            </a>
            <div className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`} aria-labelledby="dropdown01"
              style={{ minWidth: '280px' }}>
              {categories.map(cat => {
                const catProjects = getProjectsByCategory(cat.key);
                return (
                  <div key={cat.key}>
                    <a className="dropdown-item font-weight-bold" style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                      onClick={() => handleCategoryClick(cat.key)}>
                      <i className={cat.icon}></i> {translate(cat.label)}
                      <span className="float-right">
                        <i className={`fas fa-chevron-${activeCategory === cat.key ? 'up' : 'down'}`} style={{ fontSize: '10px' }}></i>
                      </span>
                    </a>
                    {activeCategory === cat.key && catProjects.length > 0 && (
                      <div className="pl-3">
                        {arraySort(catProjects, 'name').map(project => (
                          <Link className="dropdown-item" to={`/project/${project.id}`} key={project.id}
                            onClick={handleProjectClick}>
                            <img src={project.iconImage} width="30" height="30" alt="" className="mr-2" />
                            {translate(project.name)}
                          </Link>
                        ))}
                      </div>
                    )}
                    {activeCategory === cat.key && catProjects.length === 0 && (
                      <div className="pl-3">
                        <span className="dropdown-item text-muted small" style={{ cursor: 'default' }}>
                          <em>No projects yet</em>
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </li>

          {/* Dashboard links - only visible when authenticated */}
          {isAuthenticated() && (
            <>
              <li className={`nav-item dropdown ${dashboardDropdownOpen ? 'show' : ''}`} ref={dashboardDropdownRef}>
                <a className="nav-link dropdown-toggle" style={{ cursor: 'pointer' }}
                  id="dashboardDropdown"
                  onClick={() => setDashboardDropdownOpen(!dashboardDropdownOpen)}
                  aria-haspopup="true"
                  aria-expanded={dashboardDropdownOpen}>
                  <i className="fas fa-tachometer-alt"></i> Dashboard
                </a>
                <div className={`dropdown-menu ${dashboardDropdownOpen ? 'show' : ''}`} aria-labelledby="dashboardDropdown">
                  <Link className="dropdown-item" to="/dashboard/page1" onClick={() => { setNavbarOpen(false); setDashboardDropdownOpen(false); }}>
                    <i className="fas fa-file-alt"></i> Page 1
                  </Link>
                  <Link className="dropdown-item" to="/dashboard/page2" onClick={() => { setNavbarOpen(false); setDashboardDropdownOpen(false); }}>
                    <i className="fas fa-file-alt"></i> Page 2
                  </Link>
                  <div className="dropdown-divider"></div>
                  <h6 className="dropdown-header">Proyectos IA</h6>
                  <Link className="dropdown-item" to="/dashboard/cancer-classifier" onClick={() => { setNavbarOpen(false); setDashboardDropdownOpen(false); }}>
                    <i className="fas fa-brain"></i> Cancer Classifier
                  </Link>
                  <Link className="dropdown-item" to="/dashboard/people-detection" onClick={() => { setNavbarOpen(false); setDashboardDropdownOpen(false); }}>
                    <i className="fas fa-eye"></i> People Detection
                  </Link>
                  <Link className="dropdown-item" to="/dashboard/people-segmentation" onClick={() => { setNavbarOpen(false); setDashboardDropdownOpen(false); }}>
                    <i className="fas fa-user-circle"></i> People Segmentation
                  </Link>
                </div>
              </li>
            </>
          )}
        </ul>

        {/* User section - right side */}
        <ul className="navbar-nav">
          {isAuthenticated() ? (
            <li className="nav-item">
              <button className="nav-link btn btn-link text-white" onClick={handleLogout}
                style={{ cursor: 'pointer', textDecoration: 'none', border: 'none', background: 'none' }}>
                <i className="fas fa-sign-out-alt"></i> Cerrar sesión
              </button>
            </li>
          ) : (
            <li className="nav-item">
              <Link className="nav-link" to="/dashboard/login" onClick={() => setNavbarOpen(false)}>
                <i className="fas fa-sign-in-alt"></i> Login
              </Link>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default AppNavbar;
